import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { InactiveSubscriptionCard } from '../components/InactiveSubscriptionCard';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { FiPlus, FiEdit2, FiTrash2, FiTarget, FiDollarSign, FiCalendar, FiRefreshCw, FiAward } from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Metas = () => {
  const { user } = useAuth();
  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = Boolean(user?.email && ADMIN_EMAILS.includes(user.email));
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date());

  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: info básica, 2: preview dos meses
  
  // Form data - Step 1
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_amount: '',
    months: '',
    reference_date: ''
  });

  // Form data - Step 2 (preview e edição)
  const [monthlyRecords, setMonthlyRecords] = useState([]);
  const [disabledMonths, setDisabledMonths] = useState([]);

  useEffect(() => {
    if (isSubscriptionActive) {
      loadGoals();
    } else {
      setLoading(false);
    }
  }, [isSubscriptionActive]);

  const loadGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`, {
        withCredentials: true
      });
      setGoals(response.data);
    } catch (error) {
      console.error('Error loading goals:', error);
      toast.error('Erro ao carregar metas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Tem certeza? Isso também deletará os registros financeiros associados.')) return;
    
    try {
      await axios.delete(`${API}/goals/${goalId}`, {
        withCredentials: true
      });
      toast.success('Meta deletada!');
      loadGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Erro ao deletar meta');
    }
  };

  const handleRecalculate = async (goalId) => {
    try {
      const response = await axios.post(
        `${API}/goals/${goalId}/recalculate`,
        {},
        { withCredentials: true }
      );
      
      const data = response.data;
      if (data.new_records_created > 0) {
        toast.success(`${data.new_records_created} novas parcelas criadas! Valor: ${formatBRL(data.new_monthly_value)}/mês`);
      } else {
        toast.info(data.message);
      }
      loadGoals();
    } catch (error) {
      console.error('Error recalculating goal:', error);
      toast.error('Erro ao recalcular parcelas');
    }
  };

  const handleNextStep = () => {
    if (!formData.title || !formData.target_amount || !formData.months || !formData.reference_date) {
      toast.error('Preencha todos os campos');
      return;
    }

    const months = parseInt(formData.months);
    const targetAmount = parseFloat(formData.target_amount);
    const monthlyValue = targetAmount / months;
    const referenceDate = new Date(formData.reference_date + 'T00:00:00');

    // Generate monthly records preview
    const records = [];
    for (let i = 0; i < months; i++) {
      const date = new Date(referenceDate);
      date.setMonth(date.getMonth() + i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      
      records.push({
        monthKey,
        monthName,
        value: monthlyValue,
        enabled: true
      });
    }

    setMonthlyRecords(records);
    setDisabledMonths([]);
    setStep(2);
  };

  const handleToggleMonth = (monthKey) => {
    const newDisabled = disabledMonths.includes(monthKey)
      ? disabledMonths.filter(m => m !== monthKey)
      : [...disabledMonths, monthKey];
    
    setDisabledMonths(newDisabled);
    recalculateMonthlyValues(newDisabled);
  };

  const recalculateMonthlyValues = (disabled) => {
    const activeMonths = monthlyRecords.filter(r => !disabled.includes(r.monthKey));
    const totalAmount = parseFloat(formData.target_amount);
    const newMonthlyValue = totalAmount / activeMonths.length;

    setMonthlyRecords(prev => prev.map(record => ({
      ...record,
      value: disabled.includes(record.monthKey) ? 0 : newMonthlyValue,
      enabled: !disabled.includes(record.monthKey)
    })));
  };

  const handleMonthValueChange = (monthKey, newValue) => {
    // Allow free editing - store raw value
    setMonthlyRecords(prev => prev.map(record => 
      record.monthKey === monthKey ? { ...record, rawValue: newValue } : record
    ));
  };

  const handleMonthValueBlur = (monthKey, newValue) => {
    const value = parseFloat(newValue) || 0;
    
    setMonthlyRecords(prev => {
      const updated = prev.map(record => 
        record.monthKey === monthKey 
          ? { ...record, value, rawValue: undefined } 
          : record
      );
      
      // Recalculate total
      const newTotal = updated.reduce((sum, r) => sum + (r.enabled ? r.value : 0), 0);
      setFormData(f => ({ ...f, target_amount: newTotal.toString() }));
      
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      const monthlyValues = {};
      monthlyRecords.forEach(record => {
        if (record.enabled) {
          monthlyValues[record.monthKey] = record.value;
        }
      });

      const data = {
        title: formData.title,
        description: formData.description,
        target_amount: parseFloat(formData.target_amount),
        months: parseInt(formData.months),
        reference_date: formData.reference_date,
        monthly_values: monthlyValues,
        disabled_months: disabledMonths
      };

      await axios.post(`${API}/goals`, data, {
        withCredentials: true
      });

      const activeMonths = monthlyRecords.filter(r => r.enabled).length;
      toast.success(`Meta criada! ${activeMonths} registros de pagamento foram gerados.`);
      
      handleCloseDialog();
      loadGoals();
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error('Erro ao salvar meta');
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setStep(1);
    setFormData({
      title: '',
      description: '',
      target_amount: '',
      months: '',
      reference_date: ''
    });
    setMonthlyRecords([]);
    setDisabledMonths([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
            <p className="mt-4 text-blue-900 font-medium">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <main className="transition-all duration-300">
          <InactiveSubscriptionCard moduleName="Metas Financeiras" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
      <Header />
      
      <main className="transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-black text-blue-900 mb-2">Metas Financeiras</h1>
            <p className="text-slate-700">Defina metas e crie pagamentos mensais automaticamente</p>
          </div>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-goal-button"
                onClick={() => handleCloseDialog()}
                className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <FiPlus size={20} />
                Nova Meta
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-blue-900">
                  {step === 1 ? 'Nova Meta - Informações' : 'Nova Meta - Conferir Pagamentos'}
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">Preencha as informações da meta e revise as parcelas geradas automaticamente.</DialogDescription>
              </DialogHeader>

              {step === 1 ? (
                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="title" className="text-blue-900 font-semibold">Título da Meta</Label>
                    <Input
                      id="title"
                      data-testid="goal-title-input"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="Ex: Comprar um carro"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description" className="text-blue-900 font-semibold">Descrição (opcional)</Label>
                    <Textarea
                      id="description"
                      data-testid="goal-description-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-1"
                      placeholder="Detalhes sobre a meta..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="target_amount" className="text-blue-900 font-semibold">Valor Total (R$)</Label>
                      <Input
                        id="target_amount"
                        data-testid="goal-target-input"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.target_amount}
                        onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                        required
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="months" className="text-blue-900 font-semibold">Prazo (meses)</Label>
                      <Input
                        id="months"
                        data-testid="goal-months-input"
                        type="number"
                        min="1"
                        value={formData.months}
                        onChange={(e) => setFormData({ ...formData, months: e.target.value })}
                        required
                        className="mt-1"
                        placeholder="Ex: 12"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="reference_date" className="text-blue-900 font-semibold">
                      Data de Referência Inicial
                    </Label>
                    <Input
                      id="reference_date"
                      data-testid="goal-reference-date-input"
                      type="date"
                      value={formData.reference_date}
                      onChange={(e) => setFormData({ ...formData, reference_date: e.target.value })}
                      required
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-600 mt-1">
                      Esta data será replicada nos próximos meses
                    </p>
                  </div>

                  {formData.target_amount && formData.months && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-blue-900">Valor Mensal:</span>
                        <span className="text-2xl font-black text-blue-900">
                          {formatBRL(parseFloat(formData.target_amount) / parseInt(formData.months))}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-2">
                        Será criado um registro de pagamento por mês
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCloseDialog}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      data-testid="next-step-button"
                      onClick={handleNextStep}
                      className="flex-1 bg-blue-900 hover:bg-blue-800 text-white"
                    >
                      Próximo →
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 mt-4">
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Resumo da Meta</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-600">Título:</span>
                        <p className="font-semibold text-blue-900">{formData.title}</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Valor Total:</span>
                        <p className="font-semibold text-blue-900">{formatBRL(parseFloat(formData.target_amount))}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-blue-900 mb-3">
                      Pagamentos Mensais ({monthlyRecords.filter(r => r.enabled).length} meses ativos)
                    </h3>
                    <p className="text-xs text-slate-600 mb-3">
                      Desmarque os meses que não quer pagar. O valor será redistribuído automaticamente.
                    </p>
                    
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {monthlyRecords.map((record) => (
                        <div
                          key={record.monthKey}
                          className={`border rounded-lg p-3 ${
                            record.enabled ? 'border-blue-900/20 bg-white' : 'border-slate-200 bg-slate-50 opacity-60'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              data-testid={`month-checkbox-${record.monthKey}`}
                              checked={record.enabled}
                              onCheckedChange={() => handleToggleMonth(record.monthKey)}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FiCalendar className="text-blue-900" size={16} />
                                <span className="font-semibold text-blue-900 capitalize">
                                  {record.monthName}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <FiDollarSign className="text-slate-600" size={16} />
                              <Input
                                type="number"
                                step="0.01"
                                value={record.rawValue !== undefined ? record.rawValue : record.value}
                                onChange={(e) => handleMonthValueChange(record.monthKey, e.target.value)}
                                onBlur={(e) => handleMonthValueBlur(record.monthKey, e.target.value)}
                                disabled={!record.enabled}
                                className="w-28 text-right"
                                data-testid={`month-value-${record.monthKey}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-blue-900">Novo Total:</span>
                      <span className="text-2xl font-black text-blue-900">
                        {formatBRL(parseFloat(formData.target_amount))}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">
                      {monthlyRecords.filter(r => r.enabled).length} meses × média de{' '}
                      {formatBRL(parseFloat(formData.target_amount) / monthlyRecords.filter(r => r.enabled).length)}
                    </p>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1"
                    >
                      ← Voltar
                    </Button>
                    <Button
                      data-testid="save-goal-button"
                      onClick={handleSubmit}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      Criar Meta e Pagamentos
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Goals Grid */}
        {goals.length === 0 ? (
          <div className="text-center py-20">
            <FiTarget className="mx-auto text-slate-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Nenhuma meta criada</h3>
            <p className="text-slate-600 mb-6">Comece definindo suas metas financeiras</p>
            <Button
              onClick={() => setOpen(true)}
              className="bg-blue-900 hover:bg-blue-800 text-white"
            >
              Criar Primeira Meta
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {goals.map((goal) => {
              const progress = (goal.current_amount / goal.target_amount) * 100;
              const deadline = goal.deadline ? new Date(goal.deadline) : null;
              const isComplete = progress >= 100;
              
              return (
                <Card
                  key={goal.goal_id}
                  data-testid={`goal-card-${goal.goal_id}`}
                  className={`bg-white border shadow-sm ${isComplete ? 'border-green-500' : 'border-blue-900/10'}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-xl font-semibold text-blue-900 mb-1">
                          {goal.title}
                        </CardTitle>
                        {goal.description && (
                          <p className="text-sm text-slate-600 line-clamp-2">{goal.description}</p>
                        )}
                      </div>
                      <Button
                        data-testid={`delete-goal-${goal.goal_id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(goal.goal_id)}
                        className="text-red-600 hover:bg-red-50 p-2"
                      >
                        <FiTrash2 size={16} />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* 100% Completion Message */}
                      {isComplete && (
                        <div 
                          data-testid={`goal-complete-${goal.goal_id}`}
                          className="bg-green-50 border border-green-300 rounded-lg p-4 text-center"
                        >
                          <FiAward className="mx-auto text-green-600 mb-2" size={32} />
                          <p className="text-green-800 font-bold text-sm leading-relaxed">
                            Parabéns Vencedor(a)! Você é uma pessoa resiliente e não desiste dos seus sonhos, boa sorte nas próximas metas!
                          </p>
                        </div>
                      )}

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700">Progresso</span>
                          <span className={`text-sm font-bold ${isComplete ? 'text-green-600' : 'text-blue-900'}`}>{Math.min(progress, 100).toFixed(0)}%</span>
                        </div>
                        <Progress value={Math.min(progress, 100)} className="h-2" />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-600">Atual</p>
                          <p className="text-lg font-bold text-blue-900">
                            {formatBRL(goal.current_amount)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-600">Meta</p>
                          <p className="text-lg font-bold text-blue-900">
                            {formatBRL(goal.target_amount)}
                          </p>
                        </div>
                      </div>

                      {goal.monthly_value && (
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-blue-900/70 mb-1">Valor Mensal</p>
                          <p className="text-lg font-bold text-blue-900">
                            {formatBRL(goal.monthly_value)}
                          </p>
                          <p className="text-xs text-slate-600 mt-1">
                            {goal.months} meses • {goal.created_records?.length || 0} registros criados
                          </p>
                        </div>
                      )}
                      
                      {deadline && (
                        <div className="text-sm text-slate-600">
                          <span className="font-medium">Prazo: </span>
                          {deadline.toLocaleDateString('pt-BR')}
                        </div>
                      )}

                      {/* Recalculation button - show when not 100% complete */}
                      {!isComplete && (
                        <Button
                          data-testid={`recalc-goal-${goal.goal_id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleRecalculate(goal.goal_id)}
                          className="w-full border-blue-900 text-blue-900 hover:bg-blue-50 flex items-center justify-center gap-2"
                        >
                          <FiRefreshCw size={14} />
                          Gerar novas parcelas para saldo restante
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      </main>
    </div>
  );
};
